import { useCallback, useEffect, useMemo, useState } from 'react';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';

export function useSelect<T>(allRows, onSelectedRowsChange, isRowSelectable) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [allRowsSelected, setAllRowsSelected] = useState(false);

  let rows = useMemo(() => {
    if (isRowSelectable) {
      return allRows.filter((row) => isRowSelectable(row));
    }
    return allRows;
  }, [allRows, isRowSelectable]);

  useDidUpdateEffect(() => {
    if (!selectedRows.every((s) => rows.includes(selectedRows))) {
      setSelectedRows([]);
    }
  }, [rows]);

  const onSelectRow = useCallback(
    (row: T) => {
      const index = selectedRows.indexOf(row);
      if (index !== -1) {
        selectedRows.splice(index, 1);
        setSelectedRows([...selectedRows]);
        setAllRowsSelected(false);
      } else {
        setSelectedRows((rows) => [...rows, row]);
        if (selectedRows.length + 1 === rows.length) {
          setAllRowsSelected(true);
        }
      }
    },
    [selectedRows, rows],
  );

  const onSelectAllRows = useCallback(() => {
    if (selectedRows.length !== rows.length) {
      setSelectedRows(rows);
      setAllRowsSelected(true);
    } else {
      setSelectedRows([]);
      setAllRowsSelected(false);
    }
  }, [rows, selectedRows]);

  useEffect(() => {
    if (!!onSelectedRowsChange) {
      onSelectedRowsChange(selectedRows);
    }
  }, [selectedRows]);

  return useMemo(
    () => ({
      onSelectRow,
      onSelectAllRows,
      selectedRows,
      allRowsSelected,
      selectableRows: rows,
    }),
    [selectedRows, rows, onSelectedRowsChange],
  );
}
